import { FC, useMemo, ReactNode, ReactElement } from 'react'
import styles from 'components/features/pfm/GenericFloatingPane/GenericFloatingPane.styl'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'
import { FloatingPaneFooter } from 'components/features/pfm/FloatingPaneFooter/FloatingPaneFooter'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { ButtonTheme } from 'components/features/pfm/Button/Button'
import { FLOATING_PANE_CRYPTO_REWARDS_TYPE } from 'components/features/rewards/rewardsPane/constants/paneState'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { GeneralErrors } from 'interfaces/errors'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'

import resolveErrorById from 'constants/errors'

type CryptoRewardsPanePresetProps = {
  title: string
  subTitle?: string
  isLoading?: boolean
  actionName?: string
  actionTheme?: ButtonTheme
  secondaryButton?: ReactNode | null
  cancelName?: string
  cancelTheme?: ButtonTheme
  actionDisabled?: boolean
  notificationChild?: ReactNode
  progressBarPercent?: number
  iconSlug?: string
  withoutFooter?: boolean
  errors?: GeneralErrors | null
  onCancel?: () => void
  onFooterCancel?: (() => void) | null
  onSuccess?: () => void
  onErrorsOverlayClosed?: () => void
  withFooterCancel?: boolean,
  loadingStateLabel?: ReactNode,
}
export const CryptoRewardsPanePreset: FC<CryptoRewardsPanePresetProps> = (props) => {
  const {
    actionName,
    actionTheme,
    cancelName,
    cancelTheme,
    onCancel,
    onFooterCancel,
    progressBarPercent,
    isLoading,
    onSuccess,
    children,
    notificationChild,
    actionDisabled,
    title,
    subTitle,
    iconSlug,
    secondaryButton,
    withoutFooter,
    errors,
    onErrorsOverlayClosed,
    withFooterCancel = true,
    loadingStateLabel,
  } = props

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      title={ title }
      superTitle='CRYPTO REWARDS'
      iconSlug={ iconSlug }
      progressBarPercent={ progressBarPercent }
      onClose={ onCancel }
      theme='cyan'
      compact
    />
  ), [title, progressBarPercent, iconSlug, onCancel])

  const footerChild = useMemo<ReactElement | null>(() => {
    if (withoutFooter) {
      return null
    }
    return (
      <FloatingPaneFooter
        isLoading={ isLoading }
        actionName={ actionName }
        actionTheme={ actionTheme }
        actionDisabled={ actionDisabled }
        cancelName={ cancelName }
        secondaryButton={ secondaryButton }
        cancelTheme={ cancelTheme }
        onAction={ onSuccess }
        onCancel={ withFooterCancel ? onFooterCancel ?? onCancel : null }
      />
    )
  }, [onSuccess, isLoading, onCancel, onFooterCancel, actionName, secondaryButton, actionTheme, actionDisabled, cancelTheme, cancelName, withoutFooter, withFooterCancel])

  const errorsChild = useMemo<ReactNode>(() => {
    return errors?.filter(Boolean).map((error) => (
      <FloatingPaneNotification key={ error } onDismiss={ onErrorsOverlayClosed }>
        { resolveErrorById(error as any) }
      </FloatingPaneNotification>
    )) ?? null
  }, [errors, onErrorsOverlayClosed])

  return (
    <FloatingPaneLayout
      type={ FLOATING_PANE_CRYPTO_REWARDS_TYPE }
      header={ headerChild }
      footer={ footerChild }
      notification={ notificationChild ?? errorsChild }
      withStaticFooter
      bodyClassName={ styles.panePreset }
    >
      { isLoading && <LoadingOverlay>{ loadingStateLabel }</LoadingOverlay> }
      <h2>{ subTitle }</h2>
      { children }
    </FloatingPaneLayout>
  )
}
